import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface ToastItem {
  id: string
  type: ToastType
  message: string
  duration?: number
}

interface ToastContextValue {
  toasts: ToastItem[]
  showToast: (message: string, type?: ToastType, duration?: number) => void
  success: (message: string, duration?: number) => void
  error: (message: string, duration?: number) => void
  info: (message: string, duration?: number) => void
  warning: (message: string, duration?: number) => void
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const showToast = useCallback((message: string, type: ToastType = 'info', duration = 4000) => {
    const id = Math.random().toString(36).substring(2, 9)
    const newToast: ToastItem = { id, type, message, duration }

    setToasts((prev) => [...prev.slice(-4), newToast])

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }
  }, [removeToast])

  const success = useCallback((message: string, duration?: number) => showToast(message, 'success', duration), [showToast])
  const error = useCallback((message: string, duration?: number) => showToast(message, 'error', duration || 5000), [showToast])
  const info = useCallback((message: string, duration?: number) => showToast(message, 'info', duration), [showToast])
  const warning = useCallback((message: string, duration?: number) => showToast(message, 'warning', duration), [showToast])

  const value = useMemo(
    () => ({
      toasts,
      showToast,
      success,
      error,
      info,
      warning,
      removeToast,
    }),
    [toasts, showToast, success, error, info, warning, removeToast],
  )

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
